import { LayoutGrid, AlignJustify } from 'lucide-react'
import { cn } from '@mdxui/primitives/lib/utils'
import type { ComponentType, ReactNode } from 'react'

export type ViewMode = 'card' | 'list'

export interface ViewModeToggleLinkProps {
  href: string
  children: ReactNode
  className?: string
}

export interface ViewModeToggleProps {
  /** Current view mode */
  viewMode: ViewMode
  /** Callback mode — called when a mode button is clicked */
  onViewModeChange?: (mode: ViewMode) => void
  /** Link mode — returns href for each mode (used with LinkComponent) */
  viewModeHref?: (mode: ViewMode) => string
  /** Link component for framework routing (e.g. Next.js Link) */
  LinkComponent?: ComponentType<ViewModeToggleLinkProps>
  /** Additional className */
  className?: string
}

export function ViewModeToggle({ viewMode, onViewModeChange, viewModeHref, LinkComponent, className }: ViewModeToggleProps) {
  const modes: { mode: ViewMode; icon: typeof LayoutGrid; label: string }[] = [
    { mode: 'card', icon: LayoutGrid, label: 'Grid view' },
    { mode: 'list', icon: AlignJustify, label: 'List view' },
  ]

  return (
    <div className={cn('hidden md:flex items-center h-8 border border-border rounded-[var(--radius-md,calc(var(--radius)*0.75))] overflow-hidden', className)}>
      {modes.map(({ mode, icon: Icon, label }) => {
        const itemClassName = cn(
          'h-full px-2 transition-colors flex items-center justify-center',
          viewMode === mode ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground',
        )

        if (viewModeHref && LinkComponent) {
          return (
            <LinkComponent key={mode} href={viewModeHref(mode)} className={itemClassName}>
              <Icon className='size-4' />
              <span className='sr-only'>{label}</span>
            </LinkComponent>
          )
        }

        return (
          <button key={mode} type='button' onClick={() => onViewModeChange?.(mode)} className={itemClassName} aria-label={label}>
            <Icon className='size-4' />
          </button>
        )
      })}
    </div>
  )
}
